import { Plus } from "lucide-react";
import { useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { CmsBlock, CmsBlockKind, ReactableId } from "@/gen/realtime";

type Alternative = {
    kind: CmsBlockKind;
    title: string;
};

type BlockGroup = {
    name: string;
    items: Alternative[];
};

const tournamentBlocks: Alternative[] = [
    {
        kind: {
            t: "TournamentGames",
            c: {},
        },
        title: "Games for Tournament (in Container)",
    },
    {
        kind: {
            t: "TournamentRounds",
            c: {},
        },
        title: "Rounds for Tournament (in Container)",
    },
    {
        kind: {
            t: "TournamentGamesButton",
        },
        title: "Tournament Games Button",
    },
    {
        kind: {
            t: "TournamentHighlightButton",
            c: {
                gameId: "",
            },
        },
        title: "Tournament Highlight Button",
    },
    {
        kind: {
            t: "TournamentPlayersButton",
        },
        title: "Tournament Players Button",
    },
    {
        kind: {
            t: "TournamentStandingsButton",
        },
        title: "Tournament Standings Button",
    },
    {
        kind: {
            t: "TournamentPodium",
            c: {
                otherTournamentId: null,
                title: null,
            },
        },
        title: "Tournament Podium (Top 3)",
    },
];

const layoutBlocks: Alternative[] = [
    {
        kind: {
            t: "Container",
            c: {
                title: "",
                subtitle: "",
                blocks: [],
                kind: { t: "other", c: null },
                transparent: false,
            },
        },
        title: "Container",
    },
    {
        kind: {
            t: "Row",
            c: {
                sizing: { t: "showAll" },
                blocks: [],
            },
        },
        title: "Row",
    },
    {
        kind: {
            t: "Column",
            c: {
                blocks: [],
            },
        },
        title: "Column",
    },
];

const standaloneBlocks: Alternative[] = [
    { kind: { t: "GamesFromYourFavorites" }, title: "Games from your favorites" },
    {
        kind: {
            t: "Teaser",
            c: {
                imageId: null,
                title: "New Teaser",
                leadText: null,
                presentation: { t: "default" },
                teaserKind: { t: "other", c: "/" },
                label: null,
                reactableId: uuidv4() as ReactableId,
                labelColor: { t: "blue" },
                labelStyle: { t: "lg" },
            },
        },
        title: "Teaser",
    },
    {
        kind: {
            t: "AllLiveGamesButton",
        },
        title: "Button: All live games",
    },
    {
        kind: {
            t: "AllGamesButton",
        },
        title: "Button: All games",
    },
    {
        kind: {
            t: "WorldRanking",
        },
        title: "World Ranking",
    },
    {
        kind: {
            t: "LatestContent",
            c: {
                filter: {
                    t: "onlyWithVideo",
                },
                num: 3,
                title: "Latest Videos",
                subtitle: "",
            },
        },
        title: "Latest Videos",
    },
    {
        kind: {
            t: "GuessTheMoveLeaderboard",
        },
        title: "Guess The Move Leaderboard",
    },
    {
        kind: {
            t: "FeaturedStreamingChannel",
            c: {
                streamingChannelId: null,
                title: null,
                backgroundImageId: null,
            },
        },
        title: "Featured Streaming Channel",
    },
];

const blockGroups: BlockGroup[] = [
    { name: "Layout", items: layoutBlocks },
    { name: "Tournament", items: tournamentBlocks },
    { name: "Standalone", items: standaloneBlocks },
];

export const AddDialog = ({ onAdd }: { onAdd: (block: CmsBlock) => void }) => {
    const [open, setOpen] = useState(false);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button size="sm" variant="ghost">
                    <Plus size={16} />
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Add block</DialogTitle>
                </DialogHeader>
                <div className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto pr-2">
                    {blockGroups.map(group => (
                        <div key={group.name}>
                            <div className="mb-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                                {group.name}
                            </div>
                            <div className="flex flex-col gap-1">
                                {group.items.map(({ kind, title }, index) => (
                                    <Button
                                        key={`${group.name}-${index}`}
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                            onAdd({ id: uuidv4(), kind, featureFlagId: null });
                                            setOpen(false);
                                        }}
                                    >
                                        {title}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
};
